<template>
  <div class="quote-view">
    <p class="quote-eyebrow">Your personalised quote</p>
    <h2 class="quote-headline">{{ headline }}</h2>

    <template v-if="showTheftOnly">
      <!-- Theft only, opened from the offer under the recommended plan. Spells
           out what the cheaper cover leaves out before the customer commits,
           and keeps the way back to Theft + Damage one tap away. -->
      <section class="plan-card plan-card--plain">
        <p class="plan-price">
          <span class="plan-amount">
            {{ theftPrice.annual | toCurrency }}
          </span>
          <span class="plan-period">/year</span>
        </p>
        <p class="plan-ipt">including IPT</p>
        <AnnualReduction
          class="plan-badge"
          variant="badge"
          :discount="discountFor(theftPrice)"
        />

        <ul class="plan-list">
          <li
            v-for="item in theftFeatures"
            :key="item.label"
            :class="{ excluded: !item.included }"
          >
            <svg
              v-if="item.included"
              class="plan-check"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M5 13l4.5 4.5L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              class="plan-cross"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
            </svg>
            {{ item.label }}
          </li>
        </ul>
      </section>

      <section class="theft-exclusions">
        <p class="theft-exclusions-title">Also not available with Theft Only</p>
        <ul class="plan-list">
          <li v-for="item in theftExclusions" :key="item" class="excluded">
            <svg
              class="plan-cross"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
            </svg>
            {{ item }}
          </li>
        </ul>
      </section>

      <button
        type="button"
        :class="{ 'is-loading': isQuoteLoading || isSavingCover }"
        class="button ps-button is-fullwidth call-to-action plan-cta"
        data-test="continue-quick-quote-theft"
        @click="continueWith('theft')"
      >
        Continue with Theft Only
      </button>

      <button
        type="button"
        class="text-link back-to-default"
        data-test="back-to-theft-damage"
        @click="switchCover('default')"
      >
        Back to Theft + Damage
      </button>

      <p class="prices-note">Prices include IPT.</p>
    </template>

    <template v-else>
      <!-- Theft + Damage leads: its monthly price, what's included and its
           own CTA, with Theft only offered underneath as the cheaper
           alternative rather than as an equal column. Switching between the two views saves the cover type on
           the quote (switchCover), so quote setup opens on the cover the
           customer continued with. The offer only renders once the
           theft-only figure lands — never with a blank price in it. -->
      <p class="hero-price">
        <span v-if="defaultAnnual" class="plan-amount">
          {{ heroAmount | toCurrency }}
        </span>
        <span v-else class="plan-skeleton" aria-hidden="true"></span>
        <span class="hero-period">monthly</span>
      </p>
      <p v-if="defaultAnnual" class="hero-sub">
        or {{ defaultAnnual | toCurrency }} paid annually, including IPT
      </p>
      <AnnualReduction
        class="hero-banner"
        variant="banner"
        :discount="discountFor($store.getters['quote/defaultPrice'])"
      />

      <section class="included-card">
        <h3 class="included-title">Included with Theft + Damage</h3>
        <ul class="included-grid">
          <li v-for="item in includedFeatures" :key="item.label">
            <svg
              class="included-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                :d="item.icon"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ item.label }}
          </li>
        </ul>
        <p class="included-wide">
          <svg
            class="included-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              :d="familyFeature.icon"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ familyFeature.label }}
        </p>
      </section>

      <p class="proof">
        <RatingStars class="proof-stars" :size="15" />
        <span class="proof-score">{{ ratingTextShort }}</span> · 50,000+
        cyclists covered · claims assessed within 24 hours
      </p>

      <button
        type="button"
        :class="{ 'is-loading': isQuoteLoading || isSavingCover }"
        class="button ps-button is-fullwidth call-to-action plan-cta"
        data-test="continue-quick-quote"
        @click="continueWith('default')"
      >
        Protect my bike
      </button>

      <p class="time-note">
        <svg
          class="time-note-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx="12"
            cy="12"
            r="8.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          />
          <path
            d="M12 7.5V12l3 2"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Only takes about 60 seconds
      </p>

      <template v-if="theftPrice">
        <p class="or-divider"><span>or</span></p>

        <section class="theft-offer">
          <svg
            class="theft-offer-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M3.5 12.6V4.5a1 1 0 011-1h8.1a1 1 0 01.7.3l7.4 7.4a1 1 0 010 1.4l-8.1 8.1a1 1 0 01-1.4 0l-7.4-7.4a1 1 0 01-.3-.7z"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round"
            />
            <circle cx="8" cy="8" r="1.5" fill="currentColor" />
          </svg>
          <div>
            <p class="theft-offer-title">
              Looking for a lower price?<br />
              Theft Only for {{ theftPrice.annual | toCurrency }}/year
              <AnnualReduction :discount="discountFor(theftPrice)" />
            </p>
            <p class="theft-offer-copy">
              Covers theft at home and away, without damage cover.
            </p>
            <button
              type="button"
              class="text-link theft-offer-link"
              data-test="see-theft-only"
              @click="switchCover('theft')"
            >
              See Theft Only cover
            </button>
          </div>
        </section>
      </template>

      <p class="prices-note">Prices include IPT.</p>
    </template>

    <p v-if="showTheftOnly" class="proof">
      <RatingStars class="proof-stars" :size="15" />
      <span class="proof-score">{{ ratingTextShort }}</span> · 50,000+ cyclists
      covered · claims assessed within 24 hours
    </p>

    <!-- The CTAs live in the plan card and the theft-only view. One panel,
         three rows: the promise and the two cross-sells were separate cards
         competing with the CTA above them. -->
    <p class="quote-notes-title">Cover can start today.</p>
    <div class="quote-notes">
      <div class="quote-note-row">
        <div class="quote-note-head">
          <p class="quote-note-title">Price Match Promise</p>
          <a href="/price-match-promise" target="_blank" rel="noopener">
            View terms
          </a>
        </div>
        <p class="quote-note-copy">
          If you find the same cover cheaper, we’ll match it.
        </p>
      </div>

      <!-- Was a grey line of text plus `Speed icon.jpg` — a JPEG with the
           words "You'll be done in 1-2 minutes" baked into it. Now
           selectable, screen-readable text alongside the lock cross-sell. -->
      <div class="quote-note-row">
        <p class="quote-note-title">Insuring another bike?</p>
        <p class="quote-note-copy">Add it for 50% off.</p>
      </div>

      <div class="quote-note-row">
        <p class="quote-note-title">Need a lock?</p>
        <p class="quote-note-copy">Add an eligible Hiplok before checkout.</p>
      </div>
    </div>

    <p class="quote-footnote">
      <template v-if="shownAnnual < 100">
        *Premiums under £100 can only be paid for on an annual basis.
      </template>
      Including Insurance Premium Tax.
    </p>
  </div>
</template>

<script>
import RatingStars from '@/components/RatingStars'
import AnnualReduction from '@/components/quote-setup/AnnualReduction.vue'
import { coverPrice } from '@/helpers/quote-price'
import { RATING_TEXT_SHORT } from '@/helpers/rating'
export default {
  name: 'PriceInfo',
  components: { RatingStars, AnnualReduction },
  data: () => {
    return {
      ratingTextShort: RATING_TEXT_SHORT,
      // What the "Included with Theft + Damage" grid lists, each with a 24px
      // stroke icon path. Theft only's offer describes itself in a sentence
      // instead, so it doesn't read as a second plan card.
      includedFeatures: [
        {
          label: 'Theft cover',
          icon: 'M6.5 10.5h11a1 1 0 011 1v8a1 1 0 01-1 1h-11a1 1 0 01-1-1v-8a1 1 0 011-1zM8.5 10.5V7.5a3.5 3.5 0 017 0v3',
        },
        {
          label: 'Accidental damage',
          icon: 'M15.5 4.5l4 4L9 19l-4.5 1 1-4.5L15.5 4.5z',
        },
        {
          label: 'Replacement cover',
          icon: 'M19.5 12a7.5 7.5 0 01-13 5.1M4.5 12a7.5 7.5 0 0113-5.1M17.5 3.5v3.4h-3.4M6.5 20.5v-3.4h3.4',
        },
      ],
      // PS-55 approved wording. A sentence, not a two-word label, so it gets
      // its own full-width row under the grid rather than a grid cell.
      familyFeature: {
        label: 'Family members who live with you can ride your insured bikes',
        icon: 'M9 11a3 3 0 100-6 3 3 0 000 6zM3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5M16 5.2a3 3 0 010 5.6M17.5 14.7c1.8.6 3 2.3 3 4.8',
      },
      // What Theft only leaves out, listed on its own view so the customer
      // sees the gap before choosing it. These add-ons need Theft + damage;
      // the rider's cover (personal injury, loss of earnings) is still offered
      // on Theft only — see addOnsFor in helpers/quoteAddOns.js.
      theftFeatures: [
        { label: 'Theft at home', included: true },
        { label: 'Theft away from home', included: true },
        { label: 'Accidental damage not included', included: false },
        { label: 'Malicious damage not included', included: false },
      ],
      theftExclusions: [
        'Accessories & gear cover',
        'Worldwide travel or racing cover',
      ],
      // The theft-only view, in place of the recommended plan. Local to the
      // step: it isn't a step of its own, so it has no URL or GA4 step.
      showTheftOnly: false,
      // True while a Continue press waits for the cover type to be saved.
      isSavingCover: false,
      // Theft-only figure from the quote API, when the stored quote doesn't
      // carry one (see theftPrice).
      fetchedTheftPrice: null,
      theftPriceRequestedFor: null,
    }
  },
  computed: {
    quoteId() {
      return this.$store.getters['quote/quoteId']
    },
    // The quote API prices Theft only alongside Theft + damage (price.theft).
    // If the stored price or quote already has it, that's used with no call;
    // otherwise one read-only GET of the same quote fetches it. Null until
    // either lands (the step shows before createQuote() resolves), and the
    // Theft + damage card never waits on it: that price is already in the
    // store.
    theftPrice() {
      const quote = this.$store.getters['quote/quote']
      return (
        coverPrice(this.$store.getters['quote/price'], 'theft') ||
        coverPrice(quote && quote.price, 'theft') ||
        this.fetchedTheftPrice
      )
    },
    // "Your £1,000 bike, protected" — whole pounds, so the headline stays a
    // headline. Drops the value when we don't have one.
    headline() {
      if (this.showTheftOnly) return 'Theft Only cover'
      const value = Number(this.$store.getters['quote/firstBikeValue'])
      if (!value) {
        return 'Your bike, protected'
      }
      const amount = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(value)
      return `Your ${amount} bike, protected`
    },

    // Theft + damage is what the store already priced, so its card renders
    // from the first paint.
    defaultAnnual() {
      const price = this.$store.getters['quote/defaultPrice']
      return price ? price.annual : 0
    },
    // Always the monthly instalment, with the annual figure beside it; the
    // footnote covers premiums too small to pay monthly. The API's own
    // figure: monthly can cost more than a twelfth of the annual.
    heroAmount() {
      const price = this.$store.getters['quote/defaultPrice']
      return price ? price.monthly : 0
    },
    // The price on screen, for the annual-only footnote.
    shownAnnual() {
      return this.showTheftOnly ? this.theftPrice.annual : this.defaultAnnual
    },
    isQuoteLoading() {
      return this.$store.getters['quote/isQuoteLoading']
    },
  },
  watch: {
    // Kept in the store for the account step, which shows the price of the
    // chosen cover.
    theftPrice(price) {
      if (price) this.$store.commit('quote/SET_THEFT_PRICE', price)
    },
    // The offer that opens the theft-only view sits below the fold on a
    // phone, so each switch starts the new view from the top of the panel.
    showTheftOnly() {
      this.$nextTick(() => {
        const scroller = this.$el.closest('.slide-modal-scroll')
        if (scroller) scroller.scrollTop = 0
      })
    },
    // QuickQuote creates the quote before showing this step, so the id is
    // already the new one on mount (`immediate`); a later change is a new
    // quote. Only fetches when the stored quote has no theft-only figure.
    quoteId: {
      immediate: true,
      handler(id) {
        this.showTheftOnly = false
        if (id && !this.theftPrice) this.fetchTheftPrice(id)
      },
    },
  },
  mounted() {
    this.$gtm.push({
      event: 'addtowishlist',
      'prices.total_discounted': this.$store.getters['quote/price'].total,
      policy_total: this.$store.getters['quote/price'].total,
      prices: {
        discount_percent: 'test',
        total_discounted: this.$store.getters['quote/price'].total,
      },
    })
    this.$gtm.push({
      event: 'instant_quote',
    })
  },
  created() {
    // Not reactive — nothing renders it. Serialises the cover type saves, so
    // the last view the customer switched to is the last one saved.
    this.coverSave = Promise.resolve()
  },
  methods: {
    // Shown even under the monthly minimum, where annual is the only option.
    discountFor(price) {
      return (price && price.annualDiscount) || 0
    },
    switchCover(type) {
      this.showTheftOnly = type === 'theft'
      this.saveCoverType(type)
    },

    // Queued behind any save still out. A failure is logged and left: the
    // Continue press checks the saved type and tries again.
    saveCoverType(type) {
      this.coverSave = this.coverSave.then(() =>
        this.$store.dispatch('quote/setCoverType', type).catch((e) => {
          console.error('[PriceInfo] cover type save failed', e)
        })
      )
      return this.coverSave
    },

    // Continue only once the quote carries the cover being continued with.
    // If saving it still fails, the customer goes on regardless and can pick
    // the cover again on quote setup — better than a dead button.
    async continueWith(type) {
      if (this.isSavingCover) return
      this.isSavingCover = true
      try {
        await this.coverSave
        if (this.$store.getters['quote/coverType'] !== type) {
          await this.saveCoverType(type)
        }
      } finally {
        this.isSavingCover = false
      }
      this.$emit('continue', type)
    },

    // Called by the panel's header Back, like RegisterStep's: the theft-only
    // view unwinds to the recommended plan before the step itself is left.
    goBack() {
      if (this.showTheftOnly) {
        this.switchCover('default')
        return true
      }
      return false
    },

    // Best-effort: any failure just leaves the offer out.
    async fetchTheftPrice(id) {
      if (this.theftPriceRequestedFor === id) return
      this.theftPriceRequestedFor = id
      // Never show the previous quote's figure against this one.
      this.fetchedTheftPrice = null
      try {
        const quote = await this.$axios.$get(`/api/policy/v1/theft/quote/${id}`)
        // A newer quote may have replaced this one while the GET was out.
        if (id !== this.quoteId) return
        this.fetchedTheftPrice = coverPrice(quote && quote.price, 'theft')
      } catch (e) {
        // No offer rather than a broken one.
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Everything down to the CTA has to clear the fold on a phone as well as a
// laptop, so the vertical rhythm is driven by three variables that step down
// as the viewport gets shorter rather than by fixed margins.
.quote-view {
  --stack: 16px; // gap between the major blocks
  --pad: 16px; // padding inside the panels
  // The headline monthly price, and the theft-only view's annual one.
  --price: 56px;

  // Back and close live in the panel's own header row now, so the step starts
  // at the top of the scroller with only breathing room above it.
  padding: 0 0 8px;
  text-align: left;

  @media screen and (max-height: 780px) {
    --stack: 12px;
    --pad: 13px;
    --price: 48px;
  }
  @media screen and (max-height: 680px) {
    --stack: 8px;
    --pad: 10px;
    --price: 40px;
    padding-top: 14px;
  }

  // Width guard on top of the fold-budget ones above.
  @media screen and (max-width: 380px) {
    --price: 44px;
  }
}

// --- Headline -----------------------------------------------------------
.quote-eyebrow {
  font-size: $ps-text-small;
  color: $ps-muted;
}
.quote-headline {
  margin: 2px 0 0;
  font-size: 28px;
  line-height: 1.2;
  color: $ps-ink;
  letter-spacing: -0.01em;
}

// --- Price + what's included -------------------------------------------
.hero-price {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 6px;
}

.hero-period {
  font-size: 22px;
  color: $ps-body-color;
}

.plan-ipt {
  margin-top: 2px;
  font-size: 15px;
  color: $ps-muted;
}

.hero-banner {
  margin-top: 12px;
}

// Over the card's top edge, as on the payment step's annual card.
.plan-badge {
  position: absolute;
  top: -13px;
  right: 16px;
  z-index: 1;
}

.hero-sub {
  margin-top: 2px;
  font-size: 15px;
  color: $ps-muted;
}

.included-card {
  margin-top: var(--stack);
  padding: var(--pad) 20px;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;
  background: $ps-white;

  @media screen and (max-width: 360px) {
    padding-left: 14px;
    padding-right: 14px;
  }
}

.included-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: $ps-ink;
}

.included-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
  margin-top: 12px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 16px;
    line-height: 1.3;
    color: $ps-ink;
  }
}

// The family line is a sentence, not a label, so it sits under the grid
// rather than in it. Same icon, type and gap as the cells; the icon is pinned
// to the first line rather than centred on two.
.included-wide {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  font-size: 16px;
  line-height: 1.3;
  color: $ps-ink;
  // Even line lengths when it wraps. Browsers without support wrap normally.
  text-wrap: balance;
}

.included-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  color: $ps-primary;
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 12px;
}

.plan-amount {
  font-size: var(--price);
  line-height: 1.05;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: $ps-primary;
}

.plan-period {
  font-size: $ps-text-body;
  font-weight: 600;
  color: $ps-ink;
}

.plan-skeleton {
  display: inline-block;
  width: 110px;
  height: calc(var(--price) * 0.7);
  border-radius: 4px;
  background: $ps-disabled-bg;
}

.plan-list {
  margin-top: 12px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    color: $ps-ink;

    & + li {
      margin-top: 8px;
    }
  }
}

.plan-check {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  color: $ps-green;
}

.plan-cta {
  margin-top: var(--stack);
  font-weight: 700;
  border-radius: $ps-radius-md;

  @media screen and (max-height: 680px) {
    height: 50px;
  }
}

// --- "or" + cheaper alternative -----------------------------------------
.or-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: var(--stack);
  font-size: $ps-text-label;
  color: $ps-muted;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: $ps-border-color;
  }
}

.theft-offer {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: var(--stack);
  padding: var(--pad) 20px;
  border-radius: $ps-radius-md;
  background: $ps-surface;

  @media screen and (max-width: 360px) {
    padding-left: 14px;
    padding-right: 14px;
  }
}

.theft-offer-icon {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  color: $ps-muted;
}

.theft-offer-title {
  font-size: $ps-text-small;
  line-height: 1.3;
  font-weight: 700;
  color: $ps-primary;
}

.theft-offer-copy {
  margin-top: 4px;
  font-size: 15px;
  line-height: 1.45;
  color: $ps-muted;
}

// Buttons, not links: they switch views within the step rather than
// navigating. Padded out to the minimum tap target.
.text-link {
  min-height: $ps-tap-min;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: $ps-green;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: $ps-green-hover;
  }
}

.theft-offer-link {
  margin-top: 2px;
}

.time-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 15px;
  color: $ps-ink;
}

.time-note-icon {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  color: $ps-primary;
}

// --- Theft-only view ----------------------------------------------------
.plan-card--plain {
  position: relative;
  margin-top: 14px;
  padding: var(--pad) 20px 20px;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;
  background: $ps-white;

  @media screen and (max-width: 360px) {
    padding-left: 14px;
    padding-right: 14px;
  }

  .plan-price {
    margin-top: 0;
  }
}

.plan-list li.excluded {
  color: $ps-body-color;
}

.plan-cross {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  color: $ps-error;
}

.theft-exclusions {
  margin-top: var(--stack);
  padding: var(--pad) 20px;
  border-radius: $ps-radius-md;
  background: $ps-primary-25;

  .plan-list {
    margin-top: 8px;
  }
}

.theft-exclusions-title {
  font-size: 15px;
  font-weight: 700;
  color: $ps-ink;
}

.back-to-default {
  display: block;
  margin: 6px auto 0;
  color: $ps-primary;
  font-weight: 600;

  &:hover {
    color: $ps-primary-hover;
  }
}

.prices-note {
  margin-top: 10px;
  font-size: $ps-text-label;
  color: $ps-muted;
}

// --- Social proof -------------------------------------------------------
.proof {
  margin-top: var(--stack);
  font-size: 15px;
  line-height: 1.45;
  color: $ps-ink;
}
// RatingStars - brand indigo on this white card.
.proof-stars {
  margin-right: 6px;
  vertical-align: -3px;
  color: $ps-primary;
}
.proof-score {
  color: $ps-primary;
}

// --- Supporting callouts ------------------------------------------------
// One panel split by hairlines rather than three stacked cards, so the block
// under the CTA reads as footnotes to it instead of three more things to do.
.quote-notes-title {
  margin-top: calc(var(--stack) + 8px);
  font-size: $ps-text-body;
  font-weight: 700;
  color: $ps-ink;
}

.quote-notes {
  margin-top: 12px;
  border-radius: $ps-radius-md;
  background: $ps-surface;
  overflow: hidden;
}

.quote-note-row {
  padding: var(--pad) 16px;

  & + & {
    border-top: 1px solid $ps-border-color;
  }
}

// Title and link share the row, so "View terms" sits against the right edge
// on the promise row and the rows below stay flush left.
.quote-note-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.quote-note-title {
  font-size: $ps-text-small;
  font-weight: 700;
  color: $ps-ink;
}

.quote-note-copy {
  margin-top: 2px;
  font-size: $ps-text-small;
  line-height: 1.5;
  color: $ps-muted;
}

.quote-notes a {
  flex: 0 0 auto;
  font-size: $ps-text-small;
  color: $ps-primary;
  text-decoration: underline;
}

.quote-footnote {
  margin-top: 14px;
  font-size: $ps-text-label;
  line-height: 1.5;
  color: $ps-muted;
  text-align: center;
}
</style>
